import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  runTransaction,
  serverTimestamp
} from 'firebase/firestore';
import {
  BellRing,
  CheckCircle2,
  CircleDollarSign,
  CreditCard,
  History,
  Plus,
  ReceiptText,
  Search,
  WalletCards,
  X
} from 'lucide-react';
import { useAuth } from '../../core/auth/AuthProvider';
import { db } from '../../core/firebase/firebase';
import { PERMISSIONS } from '../../core/permissions/roles';

const text = (value) => String(value ?? '').trim();
const fullName = (item) =>
  text(item?.fullName || item?.fullname || item?.name) || 'Sans nom';
const money = (value) => `${Number(value || 0).toFixed(0)} DT`;

const todayIso = () => new Date().toISOString().slice(0, 10);

const dateValue = (value) => {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (value?.seconds) return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const paymentMethodLabel = (value) =>
  ({
    cash: 'Espèces',
    transfer: 'Virement bancaire',
    cheque: 'Chèque',
    card: 'TPE / carte',
    online: 'Paiement en ligne',
    multiple: 'Plusieurs modes',
    other: 'Autre'
  })[value] || value || '—';


const paymentLabelFromInstallment = (item) => {
  if (!item) return 'Paiement contrat';
  const category = text(item.category).toLowerCase();
  const label = text(item.label);

  if (category === 'registration' || /inscription/i.test(label)) {
    return 'Paiement inscription';
  }

  return label ? `Paiement ${label}` : 'Paiement contrat';
};

const paymentDisplayLabel = (payment) => {
  if (text(payment?.paymentLabel)) return text(payment.paymentLabel);
  if (payment?.creditOnly || payment?.paymentCategory === 'family_credit') {
    return 'Avance de paiement / Crédit famille';
  }
  const allocation = Array.isArray(payment?.allocations)
    ? payment.allocations[0]
    : null;
  return allocation?.label
    ? paymentLabelFromInstallment(allocation)
    : 'Paiement';
};

const paymentParts = (payment) => {
  if (Array.isArray(payment?.paymentParts) && payment.paymentParts.length) {
    return payment.paymentParts;
  }
  return [{
    amount: Number(payment?.amount || 0),
    paymentDate: payment?.paymentDate || '',
    paymentMethod: payment?.paymentMethod || 'other',
    reference: payment?.reference || ''
  }];
};

const sumPaymentsForPeriod = (payments, predicate) =>
  payments.reduce(
    (sum, payment) =>
      sum + paymentParts(payment)
        .filter((part) => predicate(String(part.paymentDate || '')))
        .reduce((partSum, part) => partSum + Number(part.amount || 0), 0),
    0
  );

const installmentRemaining = (item) =>
  Math.max(
    0,
    Number(
      item?.remainingAmount ??
        Number(item?.amount || 0) - Number(item?.paidAmount || 0)
    )
  );

function createReceiptNumber() {
  const now = new Date();
  const year = now.getFullYear();
  const stamp = `${String(now.getMonth() + 1).padStart(2, '0')}${String(
    now.getDate()
  ).padStart(2, '0')}${String(now.getHours()).padStart(2, '0')}${String(
    now.getMinutes()
  ).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;

  return `REC-${year}-${stamp}`;
}

function applyPaymentToInstallments(installments, amount, installmentId) {
  let available = Math.max(0, Number(amount || 0));
  let allocatedAmount = 0;
  const allocations = [];

  const ordered = [...(installments || [])].sort((a, b) =>
    String(a.dueDate || '').localeCompare(String(b.dueDate || ''))
  );

  const targetIds =
    installmentId && installmentId !== 'auto'
      ? [installmentId]
      : ordered.map((item) => item.id);

  const updated = (installments || []).map((item) => {
    if (!targetIds.includes(item.id) || available <= 0) {
      return item;
    }

    const due = Math.max(
      0,
      Number(
        item.remainingAmount ??
          Number(item.amount || 0) - Number(item.paidAmount || 0)
      )
    );

    if (due <= 0) return item;

    const allocated = Math.min(available, due);
    const paidAmount = Number(item.paidAmount || 0) + allocated;
    const remainingAmount = Math.max(0, Number(item.amount || 0) - paidAmount);
    const status =
      remainingAmount <= 0
        ? 'paid'
        : paidAmount > 0
          ? 'partially_paid'
          : item.status || 'upcoming';

    available -= allocated;
    allocatedAmount += allocated;

    allocations.push({
      installmentId: item.id,
      label: item.label || 'Échéance',
      dueDate: item.dueDate || null,
      amount: allocated
    });

    return {
      ...item,
      paidAmount,
      remainingAmount,
      status
    };
  });

  return {
    installments: updated,
    allocatedAmount,
    creditAmount: Math.max(0, available),
    allocations
  };
}

export default function PaymentsPanel() {
  const { profile, can } = useAuth();
  const canGlobalFinance = can(PERMISSIONS.FINANCE_GLOBAL_VIEW);

  const [parents, setParents] = useState([]);
  const [students, setStudents] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [continuingPaymentId, setContinuingPaymentId] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    parentId: '',
    studentId: '',
    contractId: '',
    installmentId: 'auto',
    amount: '',
    paymentMethod: 'cash',
    paymentDate: todayIso(),
    reference: '',
    note: '',
    creditOnly: false
  });

  useEffect(() => {
    const offParents = onSnapshot(collection(db, 'parents'), (snapshot) => {
      setParents(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    });

    const offStudents = onSnapshot(collection(db, 'students'), (snapshot) => {
      setStudents(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    });

    const offContracts = onSnapshot(collection(db, 'contracts'), (snapshot) => {
      setContracts(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    });

    const offPayments = onSnapshot(collection(db, 'payments'), (snapshot) => {
      setPayments(
        snapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => dateValue(b.createdAt) - dateValue(a.createdAt))
      );
    });

    return () => {
      offParents();
      offStudents();
      offContracts();
      offPayments();
    };
  }, []);

  const selectedParent =
    parents.find((item) => item.id === form.parentId) || null;

  const linkedStudents = students.filter(
    (student) =>
      student.primaryParentId === form.parentId ||
      student.parentId === form.parentId ||
      (Array.isArray(student.parentIds) &&
        student.parentIds.includes(form.parentId))
  );

  const linkedContracts = contracts.filter(
    (contract) =>
      contract.parentId === form.parentId &&
      (!form.studentId || contract.studentId === form.studentId)
  );

  const selectedContract =
    contracts.find((item) => item.id === form.contractId) || null;

  const openInstallments = (selectedContract?.installments || []).filter(
    (item) => installmentRemaining(item) > 0
  );

  const selectedInstallment =
    (selectedContract?.installments || []).find(
      (item) => item.id === form.installmentId
    ) || null;

  const continuingPayment =
    payments.find((item) => item.id === continuingPaymentId) || null;

  const preview = useMemo(() => {
    const amount = Math.max(0, Number(form.amount || 0));

    if (form.creditOnly || !selectedContract) {
      return {
        allocatedAmount: 0,
        creditAmount: amount,
        remainingAfter: Number(selectedContract?.remainingAmount || 0)
      };
    }

    const result = applyPaymentToInstallments(
      selectedContract.installments || [],
      amount,
      form.installmentId
    );

    return {
      allocatedAmount: result.allocatedAmount,
      creditAmount: result.creditAmount,
      remainingAfter: Math.max(
        0,
        Number(selectedContract.remainingAmount || 0) - result.allocatedAmount
      )
    };
  }, [
    form.amount,
    form.creditOnly,
    form.installmentId,
    selectedContract
  ]);

  const today = todayIso();
  const monthPrefix = today.slice(0, 7);

  const paidToday = sumPaymentsForPeriod(
    payments,
    (paymentDate) => paymentDate === today
  );

  const paidThisMonth = sumPaymentsForPeriod(
    payments,
    (paymentDate) => paymentDate.startsWith(monthPrefix)
  );

  const totalRemaining = contracts.reduce(
    (sum, contract) => sum + Number(contract.remainingAmount || 0),
    0
  );

  const totalFamilyCredit = parents.reduce(
    (sum, parent) => sum + Number(parent.familyCredit || 0),
    0
  );

  const visiblePayments = payments.filter((payment) => {
    if (!canGlobalFinance && (!form.studentId || payment.studentId !== form.studentId)) {
      return false;
    }

    const needle = search.trim().toLowerCase();
    return (
      !needle ||
      [
        payment.receiptNumber,
        payment.parentName,
        payment.studentName,
        payment.paymentMethod,
        payment.reference,
        paymentDisplayLabel(payment)
      ].some((value) => text(value).toLowerCase().includes(needle))
    );
  });

  function resetForm() {
    setContinuingPaymentId('');
    setForm({
      parentId: '',
      studentId: '',
      contractId: '',
      installmentId: 'auto',
      amount: '',
      paymentMethod: 'cash',
      paymentDate: todayIso(),
      reference: '',
      note: '',
      creditOnly: false
    });
    setError('');
  }

  function chooseParent(parentId) {
    setForm((current) => ({
      ...current,
      parentId,
      studentId: '',
      contractId: '',
      installmentId: 'auto'
    }));
  }

  function firstOpenInstallmentId(contract) {
    return (contract?.installments || []).find(
      (item) => installmentRemaining(item) > 0
    )?.id || 'auto';
  }

  function chooseStudent(studentId) {
    const firstContract = contracts
      .filter(
        (contract) =>
          contract.parentId === form.parentId &&
          contract.studentId === studentId
      )
      .sort((a, b) => dateValue(b.createdAt) - dateValue(a.createdAt))[0];

    setForm((current) => ({
      ...current,
      studentId,
      contractId: firstContract?.id || '',
      installmentId: firstOpenInstallmentId(firstContract)
    }));
  }

  function chooseContract(contractId) {
    const contract = contracts.find((item) => item.id === contractId) || null;
    setForm((current) => ({
      ...current,
      contractId,
      installmentId: firstOpenInstallmentId(contract)
    }));
  }

  function continueReceipt(payment) {
    setContinuingPaymentId(payment.id);
    setError('');
    setForm({
      parentId: payment.parentId || '',
      studentId: payment.studentId || '',
      contractId: payment.contractId || '',
      installmentId: payment.installmentId || 'auto',
      amount: '',
      paymentMethod: 'cash',
      paymentDate: todayIso(),
      reference: '',
      note: '',
      creditOnly: false
    });
    setOpen(true);
  }

  async function savePayment(event) {
    event.preventDefault();

    const amount = Number(form.amount || 0);

    if (!selectedParent) {
      setError('Choisissez un parent.');
      return;
    }

    if (!Number.isFinite(amount) || amount <= 0) {
      setError('Le montant doit être supérieur à zéro.');
      return;
    }

    if (!form.creditOnly && !selectedContract) {
      setError('Choisissez un contrat ou activez « avance non affectée ».');
      return;
    }

    if (
      !form.creditOnly &&
      form.installmentId !== 'auto' &&
      selectedInstallment &&
      amount > installmentRemaining(selectedInstallment)
    ) {
      setError(
        `Le montant dépasse le reste de cette référence (${money(
          installmentRemaining(selectedInstallment)
        )}). Enregistrez l’excédent séparément comme avance.`
      );
      return;
    }

    if (
      continuingPayment &&
      amount > Number(continuingPayment.referenceRemainingAmount || 0)
    ) {
      setError(
        `Le complément ne peut pas dépasser ${money(
          continuingPayment.referenceRemainingAmount
        )}.`
      );
      return;
    }

    if (
      ['transfer', 'cheque', 'online'].includes(form.paymentMethod) &&
      !text(form.reference)
    ) {
      setError('Une référence est obligatoire pour ce mode de paiement.');
      return;
    }

    setSaving(true);
    setError('');

    const receiptNumber = continuingPayment?.receiptNumber || createReceiptNumber();
    const paymentRef = continuingPayment
      ? doc(db, 'payments', continuingPayment.id)
      : doc(collection(db, 'payments'));
    const notificationRef = doc(collection(db, 'notifications'));
    const ledgerRef = doc(collection(db, 'familyLedger'));
    const parentRef = doc(db, 'parents', selectedParent.id);
    const contractRef = selectedContract
      ? doc(db, 'contracts', selectedContract.id)
      : null;

    try {
      console.log(
  'PAYMENT PROFILE:',
  JSON.stringify(
    {
      profileId: profile?.id,
      status: profile?.status,
      role: profile?.role,
      adminRole: profile?.adminRole,
      adminRoles: profile?.adminRoles
    },
    null,
    2
  )
);
      await runTransaction(db, async (transaction) => {
        const parentSnap = await transaction.get(parentRef);
        const parentData = parentSnap.exists() ? parentSnap.data() : {};
        const existingPaymentSnap = continuingPayment
          ? await transaction.get(paymentRef)
          : null;
        const existingPaymentData = existingPaymentSnap?.exists()
          ? existingPaymentSnap.data()
          : null;

        if (continuingPayment && !existingPaymentData) {
          throw new Error('Le reçu à compléter n’existe plus.');
        }

        let allocation = {
          installments: selectedContract?.installments || [],
          allocatedAmount: 0,
          creditAmount: amount,
          allocations: []
        };

        let contractData = selectedContract;
        let targetBefore = selectedInstallment;

        if (contractRef && !form.creditOnly) {
          const contractSnap = await transaction.get(contractRef);

          if (!contractSnap.exists()) {
            throw new Error('Le contrat sélectionné n’existe plus.');
          }

          contractData = contractSnap.data();
          targetBefore = (contractData.installments || []).find(
            (item) => item.id === form.installmentId
          ) || null;

          if (
            continuingPayment &&
            (!targetBefore || installmentRemaining(targetBefore) <= 0)
          ) {
            throw new Error('Cette référence est déjà soldée.');
          }

          allocation = applyPaymentToInstallments(
            contractData.installments || [],
            amount,
            form.installmentId
          );

          if (continuingPayment && allocation.creditAmount > 0) {
            throw new Error('Le complément dépasse le reste à payer.');
          }

          const paidAmount =
            Number(contractData.paidAmount || 0) + allocation.allocatedAmount;
          const remainingAmount = Math.max(
            0,
            Number(contractData.totalAmount || 0) - paidAmount
          );

          transaction.update(contractRef, {
            installments: allocation.installments,
            paidAmount,
            remainingAmount,
            paymentStatus:
              remainingAmount <= 0
                ? 'paid'
                : paidAmount > 0
                  ? 'partially_paid'
                  : 'unpaid',
            updatedAt: serverTimestamp()
          });
        }

        const targetAfter = form.installmentId !== 'auto'
          ? allocation.installments.find((item) => item.id === form.installmentId)
          : null;
        const paymentLabel = form.creditOnly
          ? 'Avance de paiement / Crédit famille'
          : targetBefore
            ? paymentLabelFromInstallment(targetBefore)
            : allocation.allocations.length === 1
              ? paymentLabelFromInstallment(allocation.allocations[0])
              : 'Paiement contrat';
        const paymentCategory = form.creditOnly
          ? 'family_credit'
          : targetBefore?.category || 'contract';
        const referenceTotalAmount = targetBefore
          ? Number(targetBefore.amount || 0)
          : null;
        const referenceRemainingAmount = targetAfter
          ? installmentRemaining(targetAfter)
          : form.creditOnly
            ? 0
            : null;
        const previousPaidForReference = Number(
          existingPaymentData?.referencePaidAmount ||
            existingPaymentData?.allocatedAmount ||
            0
        );
        const referencePaidAmount = continuingPayment
          ? previousPaidForReference + allocation.allocatedAmount
          : targetBefore
            ? Number(targetBefore.paidAmount || 0) + allocation.allocatedAmount
            : allocation.allocatedAmount;
        const settlementStatus = form.creditOnly
          ? 'credit'
          : referenceRemainingAmount == null
            ? 'confirmed'
            : referenceRemainingAmount <= 0
              ? 'paid'
              : 'partial';
        const paymentPart = {
          amount,
          allocatedAmount: allocation.allocatedAmount,
          creditAmount: allocation.creditAmount,
          paymentDate: form.paymentDate,
          paymentMethod: form.paymentMethod,
          reference: text(form.reference),
          createdByUid: profile?.id || null,
          createdByName: fullName(profile)
        };

        if (continuingPayment) {
          const previousParts = paymentParts(existingPaymentData);
          const previousMethod = text(existingPaymentData.paymentMethod);
          const combinedMethod =
            previousMethod && previousMethod !== form.paymentMethod
              ? 'multiple'
              : form.paymentMethod;

          transaction.update(paymentRef, {
            amount: Number(existingPaymentData.amount || 0) + amount,
            allocatedAmount:
              Number(existingPaymentData.allocatedAmount || 0) +
              allocation.allocatedAmount,
            creditAmount:
              Number(existingPaymentData.creditAmount || 0) + allocation.creditAmount,
            paymentMethod: combinedMethod,
            lastPaymentDate: form.paymentDate,
            lastPaymentMethod: form.paymentMethod,
            lastReference: text(form.reference),
            referencePaidAmount,
            referenceRemainingAmount,
            settlementStatus,
            paymentParts: [...previousParts, paymentPart],
            note: text(form.note) || existingPaymentData.note || '',
            updatedAt: serverTimestamp()
          });
        } else {
          transaction.set(paymentRef, {
            parentId: selectedParent.id,
            parentName: fullName(selectedParent),
            studentId: form.studentId || contractData?.studentId || null,
            studentName:
              (form.studentId
                ? fullName(
                    students.find((student) => student.id === form.studentId)
                  )
                : text(contractData?.studentName)) || null,
            contractId: contractRef?.id || null,
            installmentId:
              form.creditOnly || form.installmentId === 'auto'
                ? null
                : form.installmentId,
            allocations: allocation.allocations,
            amount,
            allocatedAmount: allocation.allocatedAmount,
            creditAmount: allocation.creditAmount,
            paymentMethod: form.paymentMethod,
            reference: text(form.reference),
            receiptNumber,
            paymentDate: form.paymentDate,
            lastPaymentDate: form.paymentDate,
            note: text(form.note),
            creditOnly: form.creditOnly,
            paymentLabel,
            paymentCategory,
            referenceTotalAmount,
            referencePaidAmount,
            referenceRemainingAmount,
            settlementStatus,
            paymentParts: [paymentPart],
            status: 'confirmed',
            createdByUid: profile?.id || null,
            createdByName: fullName(profile),
            createdAt: serverTimestamp()
          });
        }

        const newCredit =
          Number(parentData.familyCredit || 0) + allocation.creditAmount;

        if (allocation.creditAmount > 0) {
          transaction.update(parentRef, {
            familyCredit: newCredit,
            updatedAt: serverTimestamp()
          });
        }

        transaction.set(ledgerRef, {
          parentId: selectedParent.id,
          type:
            allocation.creditAmount > 0
              ? 'payment_with_credit'
              : continuingPayment
                ? 'payment_completion'
                : 'payment',
          direction: 'credit',
          amount,
          allocatedAmount: allocation.allocatedAmount,
          creditAmount: allocation.creditAmount,
          sourcePaymentId: paymentRef.id,
          receiptNumber,
          label: continuingPayment
            ? `Complément — ${paymentLabel}`
            : paymentLabel,
          paymentDate: form.paymentDate,
          studentId: form.studentId || contractData?.studentId || null,
          contractId: contractRef?.id || null,
          installmentId:
            form.installmentId === 'auto' ? null : form.installmentId,
          createdByUid: profile?.id || null,
          createdAt: serverTimestamp()
        });

        transaction.set(notificationRef, {
          recipientType: 'parent',
          recipientId: selectedParent.id,
          parentId: selectedParent.id,
          type: 'payment_received',
          title: continuingPayment ? 'Paiement complété' : 'Paiement enregistré',
          message:
            settlementStatus === 'partial'
              ? `${paymentLabel} : ${money(referencePaidAmount)} payé(s), reste ${money(referenceRemainingAmount)}.`
              : settlementStatus === 'paid'
                ? `${paymentLabel} est maintenant soldé. Montant payé : ${money(referencePaidAmount)}.`
                : allocation.creditAmount > 0
                  ? `Une avance de ${money(amount)} a été ajoutée à votre crédit famille.`
                  : `Un paiement de ${money(amount)} a été enregistré${
                      contractData?.studentName ? ` pour ${contractData.studentName}` : ''
                    }.` ,
          paymentId: paymentRef.id,
          contractId: contractRef?.id || null,
          studentId: contractData?.studentId || form.studentId || null,
          read: false,
          createdAt: serverTimestamp()
        });
      });

      setMessage(
        continuingPayment
          ? `Reçu ${receiptNumber} mis à jour.`
          : `Paiement enregistré — reçu ${receiptNumber}.`
      );
      setOpen(false);
      if (canGlobalFinance) {
        resetForm();
      } else {
        setContinuingPaymentId('');
        setForm((current) => ({
          ...current,
          amount: '',
          paymentMethod: 'cash',
          paymentDate: todayIso(),
          reference: '',
          note: ''
        }));
      }

      window.setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error(err);
      setError(`Paiement impossible : ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      {message && (
        <div className="success-note payment-toast">
          <CheckCircle2 size={18} />
          {message}
        </div>
      )}

      {canGlobalFinance && <section className="payment-kpis">
        <article>
          <CircleDollarSign />
          <div>
            <span>Encaissements du jour</span>
            <strong>{money(paidToday)}</strong>
          </div>
        </article>

        <article>
          <CreditCard />
          <div>
            <span>Encaissements du mois</span>
            <strong>{money(paidThisMonth)}</strong>
          </div>
        </article>

        <article>
          <History />
          <div>
            <span>Reste à encaisser</span>
            <strong>{money(totalRemaining)}</strong>
          </div>
        </article>

        <article>
          <WalletCards />
          <div>
            <span>Crédit des familles</span>
            <strong>{money(totalFamilyCredit)}</strong>
          </div>
        </article>
      </section>}

      {!canGlobalFinance && <div className="panel finance-targeted-notice">
        <strong>Accès ciblé aux paiements</strong>
        <p>Sélectionnez un parent puis un enfant. Aucun total global de caisse ou de revenus n’est affiché dans cet espace.</p>
      </div>}

      <section className="panel payments-history-panel">
        <div className="panel-heading payments-heading">
          <div>
            <p className="eyebrow">{canGlobalFinance ? 'CAISSE' : 'ÉLÈVE SÉLECTIONNÉ'}</p>
            <h2>{canGlobalFinance ? 'Historique des paiements' : 'Paiements de l’élève sélectionné'}</h2>
          </div>

          <div className="payments-toolbar">
            <label className="student-search">
              <Search size={17} />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={canGlobalFinance ? 'Reçu, parent, enfant…' : 'Référence ou reçu…'}
              />
            </label>

            <button
              type="button"
              className="button button-primary"
              onClick={() => {
                if (canGlobalFinance) resetForm();
                setOpen(true);
              }}
            >
              <Plus size={17} />
              Enregistrer un paiement
            </button>
          </div>
        </div>

        <div className="payments-list">
          {visiblePayments.length ? (
            visiblePayments.map((payment) => (
              <article className="payment-history-row" key={payment.id}>
                <span className="payment-receipt-icon">
                  <ReceiptText size={18} />
                </span>

                <div>
                  <strong>{payment.receiptNumber}</strong>
                  <small>{paymentDisplayLabel(payment)}</small>
                  <small>
                    {payment.parentName}
                    {payment.studentName
                      ? ` · ${payment.studentName}`
                      : ''}
                  </small>
                </div>

                <div>
                  <strong>{money(payment.amount)}</strong>
                  <small>
                    {paymentMethodLabel(payment.paymentMethod)}
                  </small>
                  {payment.referenceTotalAmount != null && (
                    <small>
                      Sur {money(payment.referenceTotalAmount)}
                    </small>
                  )}
                </div>

                <div className="payment-history-status">
                  <strong>{payment.lastPaymentDate || payment.paymentDate || '—'}</strong>
                  <small>
                    {payment.settlementStatus === 'partial'
                      ? `Reste : ${money(payment.referenceRemainingAmount)}`
                      : payment.settlementStatus === 'paid'
                        ? 'Soldé'
                        : Number(payment.creditAmount || 0) > 0
                          ? `Crédit : ${money(payment.creditAmount)}`
                          : `Affecté : ${money(payment.allocatedAmount)}`}
                  </small>
                  {payment.settlementStatus === 'partial' &&
                    payment.contractId &&
                    payment.installmentId && (
                      <button
                        type="button"
                        className="button button-secondary payment-complete-button"
                        onClick={() => continueReceipt(payment)}
                      >
                        Compléter le reçu
                      </button>
                    )}
                </div>
              </article>
            ))
          ) : (
            <div className="compact-empty">
              {!canGlobalFinance && !form.studentId ? 'Sélectionnez un enfant pour afficher ses paiements.' : 'Aucun paiement enregistré.'}
            </div>
          )}
        </div>
      </section>

      {open && (
        <div className="modal-layer">
          <form
            className="task-create-modal panel payment-modal"
            onSubmit={savePayment}
          >
            <header>
              <div>
                <p className="eyebrow">{continuingPayment ? 'COMPLÉMENT' : 'NOUVEL ENCAISSEMENT'}</p>
                <h2>{continuingPayment ? `Compléter ${continuingPayment.receiptNumber}` : 'Enregistrer un paiement'}</h2>
              </div>

              <button
                type="button"
                className="drawer-close"
                onClick={() => setOpen(false)}
                aria-label="Fermer"
              >
                <X size={20} />
              </button>
            </header>

            <div className="task-create-body">
              {error && <div className="alert alert-error">{error}</div>}

              <div className="form-two-columns">
                <label>
                  Parent
                  <select
                    value={form.parentId}
                    onChange={(event) =>
                      chooseParent(event.target.value)
                    }
                    disabled={Boolean(continuingPayment)}
                    required
                  >
                    <option value="">Choisir un parent</option>
                    {[...parents]
                      .sort((a, b) =>
                        fullName(a).localeCompare(
                          fullName(b),
                          'fr'
                        )
                      )
                      .map((parent) => (
                        <option
                          key={parent.id}
                          value={parent.id}
                        >
                          {fullName(parent)}
                        </option>
                      ))}
                  </select>
                </label>

                <label>
                  Enfant
                  <select
                    value={form.studentId}
                    onChange={(event) =>
                      chooseStudent(event.target.value)
                    }
                    disabled={!form.parentId || Boolean(continuingPayment)}
                  >
                    <option value="">Famille / non affecté</option>
                    {linkedStudents.map((student) => (
                      <option
                        key={student.id}
                        value={student.id}
                      >
                        {fullName(student)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <label>
                Contrat
                <select
                  value={form.contractId}
                  onChange={(event) => chooseContract(event.target.value)}
                  disabled={form.creditOnly || !form.parentId || Boolean(continuingPayment)}
                >
                  <option value="">Choisir un contrat</option>
                  {linkedContracts.map((contract) => (
                    <option
                      key={contract.id}
                      value={contract.id}
                    >
                      {contract.studentName} —{' '}
                      {money(contract.remainingAmount)} restant
                    </option>
                  ))}
                </select>
              </label>

              {!continuingPayment && (
              <label className="payment-credit-toggle">
                <input
                  type="checkbox"
                  checked={form.creditOnly}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      creditOnly: event.target.checked,
                      contractId: event.target.checked
                        ? ''
                        : current.contractId,
                      installmentId: 'auto'
                    }))
                  }
                />
                Enregistrer comme avance non affectée / crédit famille
              </label>
              )}

              {!form.creditOnly && selectedContract && (
                <label>
                  Affectation / référence du paiement
                  <select
                    value={form.installmentId}
                    disabled={Boolean(continuingPayment)}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        installmentId: event.target.value
                      }))
                    }
                  >
                    <option value="auto">
                      Affecter automatiquement aux échéances les plus anciennes
                    </option>
                    {openInstallments.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.label} —{' '}
                        {money(
                          item.remainingAmount ??
                            Number(item.amount || 0) -
                              Number(item.paidAmount || 0)
                        )}{' '}
                        restant
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {continuingPayment && (
                <div className="payment-reference-banner">
                  <div>
                    <span>Référence</span>
                    <strong>{paymentDisplayLabel(continuingPayment)}</strong>
                  </div>
                  <div>
                    <span>Déjà payé</span>
                    <strong>{money(continuingPayment.referencePaidAmount || continuingPayment.amount)}</strong>
                  </div>
                  <div>
                    <span>Reste à payer</span>
                    <strong>{money(continuingPayment.referenceRemainingAmount)}</strong>
                  </div>
                </div>
              )}

              <div className="form-two-columns">
                <label>
                  Montant reçu
                  <input
                    type="number"
                    min="0.001"
                    step="0.001"
                    value={form.amount}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        amount: event.target.value
                      }))
                    }
                    required
                  />
                </label>

                <label>
                  Date du paiement
                  <input
                    type="date"
                    value={form.paymentDate}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        paymentDate: event.target.value
                      }))
                    }
                    required
                  />
                </label>
              </div>

              <div className="form-two-columns">
                <label>
                  Mode de paiement
                  <select
                    value={form.paymentMethod}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        paymentMethod: event.target.value
                      }))
                    }
                  >
                    <option value="cash">Espèces</option>
                    <option value="transfer">
                      Virement bancaire
                    </option>
                    <option value="cheque">Chèque</option>
                    <option value="card">TPE / carte</option>
                    <option value="online">
                      Paiement en ligne
                    </option>
                    <option value="other">Autre</option>
                  </select>
                </label>

                <label>
                  Référence bancaire / chèque
                  <input
                    value={form.reference}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        reference: event.target.value
                      }))
                    }
                    placeholder="N° chèque, virement…"
                  />
                </label>
              </div>

              <label>
                Note
                <textarea
                  value={form.note}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      note: event.target.value
                    }))
                  }
                  placeholder="Observation facultative…"
                />
              </label>

              <div className="payment-preview">
                <div>
                  <span>{continuingPayment ? 'Complément affecté' : 'Montant affecté'}</span>
                  <strong>{money(preview.allocatedAmount)}</strong>
                </div>
                <div>
                  <span>Crédit généré</span>
                  <strong>{money(preview.creditAmount)}</strong>
                </div>
                <div>
                  <span>Reste du contrat après paiement</span>
                  <strong>{money(preview.remainingAfter)}</strong>
                </div>
              </div>

              <div className="family-form-footer">
                <button
                  type="button"
                  className="button button-secondary"
                  onClick={() => setOpen(false)}
                >
                  Annuler
                </button>

                <button
                  type="submit"
                  className="button button-primary"
                  disabled={saving}
                >
                  <BellRing size={17} />
                  {saving
                    ? 'Enregistrement…'
                    : continuingPayment
                      ? 'Enregistrer le complément'
                      : 'Confirmer et notifier le parent'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
